import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { api, type PortalDocument } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { useDepartmentName, useDepartmentStore } from '../../store/departmentStore';
import { ConfirmDialog } from '../ConfirmDialog';
import { EmptyState, PageHeader } from './portalParts';
import { timeAgo } from './portalStyles';

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** File type label from the file name, e.g. "PDF", "DOCX". */
function fileKind(name: string): string {
  const ext = name.split('.').pop();
  return ext && ext !== name ? ext.toUpperCase() : 'File';
}

/** Upload (no `editing`) or edit the details of a document. Admins only. */
function DocumentDialog({
  open,
  editing,
  defaultCategory,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: PortalDocument | null;
  defaultCategory: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const departments = useDepartmentStore((s) => s.departments);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const fill = () => {
    setFile(null);
    setTitle(editing?.title ?? '');
    setDescription(editing?.description ?? '');
    setCategory(editing?.category ?? defaultCategory);
    setError(null);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    const meta = { title: title.trim(), description, category };
    try {
      if (editing) await api.documents.update(editing.id, meta);
      else if (file) await api.documents.upload(file, { ...meta, title: meta.title || file.name.replace(/\.[^.]+$/, '') });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the document');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="document-title"
      slotProps={{ transition: { onEnter: fill } }}
    >
      <DialogTitle id="document-title" component="h2" variant="h6">
        {editing ? 'Edit document' : 'Upload document'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {!editing && (
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <Button variant="outlined" color="neutral" startIcon={<CloudUploadOutlinedIcon />} onClick={() => fileInput.current?.click()}>
                Choose file
              </Button>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                {file ? `${file.name} · ${formatSize(file.size)}` : 'PDF, Office, text, CSV or image · up to 25 MB'}
              </Typography>
              <input
                ref={fileInput}
                type="file"
                hidden
                accept={ACCEPT}
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setFile(f);
                  if (f && !title.trim()) setTitle(f.name.replace(/\.[^.]+$/, ''));
                }}
              />
            </Stack>
          )}
          <TextField
            label="Title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 160 } }}
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <TextField
            select
            label="Department"
            fullWidth
            value={departments.some((d) => d.id === category) ? category : ''}
            onChange={(e) => setCategory(e.target.value)}
          >
            {departments.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="neutral" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || (editing ? !title.trim() : !file)}
          onClick={() => void save()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {editing ? 'Save' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** One row in the documents list: details, download and (admins) edit / delete. */
export function DocumentRow({
  doc,
  onEdit,
  onDelete,
}: {
  doc: PortalDocument;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const departmentName = useDepartmentName();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ alignItems: 'center', px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: 'chrome.tint',
          color: 'primary.main',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          fontSize: 10,
          fontWeight: 700,
        }}
        aria-hidden
      >
        {fileKind(doc.file_name)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
          {doc.title}
        </Typography>
        {doc.description && (
          <Typography variant="caption" component="div" noWrap>
            {doc.description}
          </Typography>
        )}
        <Typography variant="caption" component="div" color="text.secondary" noWrap>
          {departmentName(doc.category)} · {formatSize(doc.size)} · {timeAgo(doc.updated_at ?? doc.created_at)}
          {doc.uploaded_by_name ? ` · ${doc.uploaded_by_name}` : ''}
        </Typography>
      </Box>
      <Tooltip title={`Download ${doc.file_name}`}>
        <IconButton component="a" href={api.documents.fileUrl(doc.id)} aria-label={`Download ${doc.title}`}>
          <FileDownloadOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {onEdit && (
        <Tooltip title="Edit details">
          <IconButton onClick={onEdit} aria-label={`Edit ${doc.title}`}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title="Delete">
          <IconButton onClick={onDelete} aria-label={`Delete ${doc.title}`}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

/** Company documents library: policies, forms and handbooks, filterable by department. */
export function DocumentsPage() {
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const departments = useDepartmentStore((s) => s.departments);
  const [docs, setDocs] = useState<PortalDocument[] | null>(null);
  const [storageEnabled, setStorageEnabled] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ editing: PortalDocument | null } | null>(null);
  const [deleting, setDeleting] = useState<PortalDocument | null>(null);

  const load = useCallback(
    () =>
      api.documents
        .list()
        .then((r) => {
          setDocs(r.documents);
          setStorageEnabled(r.storageEnabled);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Could not load documents');
          setDocs([]);
        }),
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const visible = (docs ?? []).filter((d) => filter === 'all' || d.category === filter);
  const usedDepartments = departments.filter((d) => (docs ?? []).some((doc) => doc.category === d.id));

  return (
    <>
      <PageHeader
        title="Documents"
        description="Policies, forms and handbooks to download."
        icon={<DescriptionOutlinedIcon color="primary" />}
        actions={
          isAdmin && (
            <Button
              variant="contained"
              startIcon={<CloudUploadOutlinedIcon />}
              disabled={!storageEnabled}
              onClick={() => setDialog({ editing: null })}
            >
              Upload document
            </Button>
          )
        }
      />
      {!storageEnabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {isAdmin
            ? 'Uploads are off until file storage is enabled: turn on R2 in the Cloudflare dashboard, then connect the bucket.'
            : 'The documents library isn’t set up yet.'}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {usedDepartments.length > 1 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip
            label="All"
            color={filter === 'all' ? 'primary' : 'default'}
            variant={filter === 'all' ? 'filled' : 'outlined'}
            onClick={() => setFilter('all')}
          />
          {usedDepartments.map((d) => (
            <Chip
              key={d.id}
              label={d.name}
              color={filter === d.id ? 'primary' : 'default'}
              variant={filter === d.id ? 'filled' : 'outlined'}
              onClick={() => setFilter(d.id)}
            />
          ))}
        </Stack>
      )}
      {docs === null ? (
        <Stack spacing={1}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={64} />
          ))}
        </Stack>
      ) : visible.length ? (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper', overflow: 'hidden' }}>
          {visible.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              onEdit={isAdmin ? () => setDialog({ editing: doc }) : undefined}
              onDelete={isAdmin ? () => setDeleting(doc) : undefined}
            />
          ))}
        </Box>
      ) : (
        <EmptyState>{isAdmin ? 'No documents yet. Upload the first one.' : 'No documents have been shared yet.'}</EmptyState>
      )}

      <DocumentDialog
        open={Boolean(dialog)}
        editing={dialog?.editing ?? null}
        defaultCategory={filter === 'all' ? 'general' : filter}
        onClose={() => setDialog(null)}
        onSaved={() => void load()}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete document?"
        message={`“${deleting?.title ?? ''}” will be removed for everyone, and from any announcement it's attached to.`}
        confirmLabel="Delete"
        destructive
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          const doc = deleting;
          setDeleting(null);
          if (doc) {
            void api.documents
              .remove(doc.id)
              .then(load)
              .catch((err) => setError(err instanceof Error ? err.message : 'Could not delete'));
          }
        }}
      />
    </>
  );
}
